<template>
    <v-container id="confernote" class="confernote-section">

        <!-- 섹션 헤더 -->
        <h2 class="text-center mb-7">
            <span class="d-block primary--text mb-2" data-aos="fade-up">Our Product</span>
            <p data-aos="fade-up" data-aos-delay="150">AI Meeting Solution</p>
        </h2>

        <v-card
            class="product-card rounded-xl pa-8 pa-md-12 mb-6"
            data-aos="fade-up"
            data-aos-delay="200"
            elevation="0"
        >
            <v-row align="start">

                <!-- ── 좌측: 제품명 + 소개 + 신청 폼 ── -->
                <v-col cols="12" md="5" class="pr-md-8">

                    <!-- 로고 + 제품명 + 한글명 한 줄 -->
                    <div class="d-flex align-center mb-4">
                        <v-img
                            :src="require('@/assets/2026/favicon-32x32.png')"
                            width="36"
                            height="36"
                            max-width="36"
                            contain
                            class="product-logo mr-2"
                        />
                        <div class="d-flex align-baseline">
                            <span class="product-name">ConferNote</span>
                            <span class="product-name-ko ml-2">컨퍼노트</span>
                        </div>
                    </div>

                    <div class="badges mb-5">
                        <v-chip small class="mr-2 mb-2 badge-chip" color="primary" outlined>
                            GS인증 획득
                        </v-chip>
                        <v-chip small class="mb-2 badge-chip" color="primary" outlined>
                            SMPP 선정 제품
                        </v-chip>
                    </div>

                    <p class="product-desc">
                        AI가 회의 음성을 자동으로 기록하고, 회의록 초안 생성·핵심 요약·질의 기반 탐색 기능을 제공하는 AI 기반 회의 지원 솔루션입니다.
                    </p>
                    <p class="product-desc secondary-desc mt-2 mb-6">
                        폐쇄망(On-Premise) 환경 구축을 지원하여 보안이 중요한 공공기관에서도 안전하게 운영 가능합니다.
                    </p>

                    <!-- 데모 신청 폼 -->
                    <div class="demo-form-wrap rounded-xl pa-5 mb-4">
                        <div class="demo-form-title mb-4">
                            <v-icon small color="primary" class="mr-1">mdi-laptop</v-icon>
                            Service Demo 신청
                        </div>

                        <v-expand-transition>
                            <div v-if="formState === 'success'" class="form-success rounded-lg pa-4 mb-2">
                                <v-icon color="success" class="mr-2">mdi-check-circle-outline</v-icon>
                                신청이 완료되었습니다. 확인 후 연락드리겠습니다.
                            </div>
                        </v-expand-transition>

                        <div v-if="formState !== 'success'">
                            <v-text-field
                                v-model="form.company"
                                label="소속" placeholder="회사명 / 기관명"
                                outlined dense rounded
                                :error-messages="errors.company"
                                class="form-field"
                                @input="clearError('company')"
                            />
                            <v-text-field
                                v-model="form.name"
                                label="성명" placeholder="홍길동"
                                outlined dense rounded
                                :error-messages="errors.name"
                                class="form-field"
                                @input="clearError('name')"
                            />
                            <v-text-field
                                v-model="form.phone"
                                label="연락처" placeholder="010-0000-0000"
                                outlined dense rounded
                                :error-messages="errors.phone"
                                class="form-field"
                                @input="clearError('phone')"
                            />
                            <v-text-field
                                v-model="form.email"
                                label="이메일" placeholder="example@company.com"
                                outlined dense rounded
                                :error-messages="errors.email"
                                class="form-field"
                                @input="clearError('email')"
                            />


                            <v-expand-transition>
                                <div v-if="formState === 'error'" class="form-error rounded-lg pa-3 mb-3">
                                    <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
                                    전송 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.
                                </div>
                            </v-expand-transition>

                            <v-btn
                                color="primary" rounded large block elevation="0"
                                :loading="formState === 'loading'"
                                :disabled="formState === 'loading'"
                                class="submit-btn"
                                @click="submitForm"
                            >
                                <v-icon left>mdi-send-outline</v-icon>
                                신청하기
                            </v-btn>
                        </div>
                    </div>

                    <!-- 공급사 및 문의처 -->
                    <div class="supplier-info rounded-xl pa-4">
                        <div class="supplier-company mb-2">
                            주식회사 코드라인
                            <span class="supplier-company-en">CodeLine Co., Ltd.</span>
                        </div>
                        <div class="supplier-row">
                            <v-icon x-small color="primary" class="mr-1">mdi-account-outline</v-icon>
                            <span class="supplier-label">담당자</span>
                            <span class="supplier-value">이상돈</span>
                        </div>
                        <div class="supplier-row">
                            <v-icon x-small color="primary" class="mr-1">mdi-email-outline</v-icon>
                            <span class="supplier-label">이메일</span>
                            <span class="supplier-value">sdlee@code-line.kr</span>
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        icon x-small class="ml-1 copy-btn"
                                        v-on="on"
                                        @click="copyEmail"
                                    >
                                        <v-icon x-small>{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ copied ? '복사됨!' : '이메일 복사' }}</span>
                            </v-tooltip>
                        </div>
                    </div>

                </v-col>

                <!-- ── 우측: 기능 카드 6개 + Key Screens 4개 ── -->
                <v-col cols="12" md="7" class="mt-8 mt-md-0">

                    <!-- 기능 카드 6개 -->
                    <v-row dense class="mb-8">
                        <v-col
                            v-for="(feature, i) in product.features"
                            :key="'f'+i"
                            cols="6" sm="4"
                        >
                            <v-hover v-slot="{ hover }">
                                <v-card
                                    class="feature-card rounded-xl pa-4 pa-md-5"
                                    :class="{ 'feature-on-hover': hover }"
                                    elevation="0"
                                >
                                    <v-icon class="feature-icon mb-3" :color="hover ? 'white' : 'black'">
                                        {{ feature.icon }}
                                    </v-icon>
                                    <div class="feature-title">{{ feature.title }}</div>
                                    <div class="feature-desc">{{ feature.desc }}</div>
                                </v-card>
                            </v-hover>
                        </v-col>
                    </v-row>

                    <!-- Key Screens 타이틀 -->
                    <div class="mb-4">
                        <span class="rounded-pill primary px-6 py-2 white--text screens-title-pill">
                            Key Screens
                        </span>
                    </div>

                    <!-- 화면 카드 4개 -->
                    <v-row dense>
                        <v-col
                            v-for="(screen, i) in product.screens"
                            :key="'s'+i"
                            cols="12" sm="6"
                        >
                            <v-hover v-slot="{ hover }">
                                <v-card
                                    class="screen-card rounded-xl pa-4"
                                    :class="{ 'screen-on-hover': hover }"
                                    elevation="0"
                                    @click="openLightbox(i)"
                                    style="cursor: pointer;"
                                >
                                    <div class="d-flex align-center justify-space-between mb-3">
                                        <div class="screen-num">0{{ i + 1 }}</div>
                                        <v-chip
                                            x-small class="screen-type-chip"
                                            :color="hover ? 'white' : 'black'"
                                            :text-color="hover ? 'black' : 'white'"
                                        >
                                            {{ screen.type }}
                                        </v-chip>
                                    </div>
                                    <v-icon class="screen-icon mb-2" :color="hover ? 'white' : 'black'">
                                        {{ screen.icon }}
                                    </v-icon>
                                    <div class="screen-title">{{ screen.title }}</div>
                                    <div class="screen-desc">{{ screen.desc }}</div>
                                    <div class="screen-view-hint mt-2">
                                        <v-icon x-small :color="hover ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.2)'">mdi-magnify-plus-outline</v-icon>
                                        <span class="screen-view-text ml-1" :style="hover ? 'color:rgba(255,255,255,0.5)' : 'color:rgba(0,0,0,0.2)'">클릭하여 화면 보기</span>
                                    </div>
                                </v-card>
                            </v-hover>
                        </v-col>
                    </v-row>

                </v-col>

            </v-row>
        </v-card>

        <!-- 라이트박스 -->
        <vue-easy-lightbox
            escDisabled
            moveDisabled
            :visible="visible"
            :imgs="imgs"
            :index="index"
            @hide="visible = false"
        ></vue-easy-lightbox>

    </v-container>
</template>

<script>
import { mapGetters } from 'vuex';

const APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzKFZzy24puv0xb_ClB21vVGdvW5Sm1djYD1waFuXxKeWDyMbSSGRPNNw3vt6dMmLXSWg/exec';

export default {
    name: 'ConferNote',
    data: () => ({
        copied: false,
        visible: false,
        imgs: [],
        index: 0,
        formState: 'idle',
        form: { company: '', name: '', phone: '', email: '' },
        errors: { company: '', name: '', phone: '', email: '' },
    }),
    computed: {
        ...mapGetters(['confernote']),
        product() { return this.confernote; },
    },
    methods: {
        openLightbox(i) {
            this.imgs = this.product.screens.map((s) => require(`@/assets/${s.img}.png`));
            this.index = i;
            this.visible = true;
        },
        copyEmail() {
            navigator.clipboard.writeText('sdlee@code-line.kr').then(() => {
                this.copied = true;
                setTimeout(() => { this.copied = false; }, 2000);
            });
        },
        clearError(field) { this.errors[field] = ''; },
        validate() {
            let valid = true;
            this.errors = { company: '', name: '', phone: '', email: '' };
            if (!this.form.company.trim()) { this.errors.company = '소속을 입력해 주세요.';  valid = false; }
            if (!this.form.name.trim())    { this.errors.name    = '성명을 입력해 주세요.';   valid = false; }
            if (!this.form.phone.trim())   { this.errors.phone   = '연락처를 입력해 주세요.'; valid = false; }
            if (!this.form.email.trim()) {
                this.errors.email = '이메일을 입력해 주세요.'; valid = false;
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
                this.errors.email = '이메일 형식을 확인해 주세요.'; valid = false;
            }
            return valid;
        },
        async submitForm() {
            if (!this.validate()) return;
            this.formState = 'loading';
            try {
                await fetch(APPS_SCRIPT_URL, { method: 'POST', body: JSON.stringify(this.form) });
                this.formState = 'success';
            } catch (err) {
                this.formState = 'error';
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.confernote-section {
    padding-top: 4rem;
    padding-bottom: 2rem;
}

h2 {
    font-family: 'Prompt', sans-serif;
    font-size: 1.85rem;
    span { font-size: 1.18rem; font-weight: 400; }
    p { line-height: 1.3; }
}

.product-card {
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    background-color: rgba(0, 0, 0, 0.015);
    overflow: visible !important;
}

/* 로고 */
.product-logo {
    flex-shrink: 0;
    border-radius: 8px;
}

.product-name {
    font-family: 'Prompt', sans-serif;
    font-size: 2.2rem;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.75);
    line-height: 1.1;
    letter-spacing: -0.5px;
}

.product-name-ko {
    font-family: 'S-CoreDream', sans-serif;
    font-size: 0.82rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.75);
    letter-spacing: 2px;
    white-space: nowrap;
}

.badge-chip {
    font-family: 'S-CoreDream', sans-serif;
    font-size: 0.72rem !important;
    font-weight: 600 !important;
    letter-spacing: 0.3px;
}

.product-desc {
    font-size: 0.92rem;
    line-height: 1.8;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 0;
}

.secondary-desc {
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.4);
}

/* 데모 신청 폼 */
.demo-form-wrap {
    background-color: rgba(0, 0, 0, 0.025);
    border: 1px solid rgba(0, 0, 0, 0.08);

    .demo-form-title {
        font-family: 'Prompt', sans-serif;
        font-size: 0.88rem;
        font-weight: 600;
        color: var(--v-primary-base);
        letter-spacing: 0.3px;
    }
}

.form-field {
    margin-bottom: -8px !important;
    ::v-deep .v-input__slot { font-size: 0.8rem; }
    ::v-deep .v-label { font-size: 0.8rem; }
    ::v-deep input, ::v-deep textarea { font-size: 0.8rem !important; }
}

.form-success {
    background-color: rgba(76, 175, 80, 0.08);
    border: 1px solid rgba(76, 175, 80, 0.3);
    font-size: 0.82rem;
    color: #2e7d32;
}

.form-error {
    background-color: rgba(244, 67, 54, 0.06);
    border: 1px solid rgba(244, 67, 54, 0.25);
    font-size: 0.82rem;
    color: #c62828;
}

.submit-btn {
    font-family: 'S-CoreDream', sans-serif;
    font-weight: 600;
    letter-spacing: 0.5px;
    margin-top: 4px;
}

/* 공급사 및 문의처 */
.supplier-info {
    background-color: rgba(0, 0, 0, 0.02);
    border: 1px solid rgba(0, 0, 0, 0.07);

    .supplier-company {
        font-family: 'S-CoreDream', sans-serif;
        font-size: 0.85rem;
        font-weight: 700;
        color: rgba(0, 0, 0, 0.7);
    }

    .supplier-company-en {
        font-family: 'Prompt', sans-serif;
        font-size: 0.75rem;
        font-weight: 400;
        color: rgba(0, 0, 0, 0.35);
        margin-left: 6px;
    }

    .supplier-row {
        display: flex;
        align-items: center;
        font-size: 0.8rem;
        color: rgba(0, 0, 0, 0.55);
        margin-top: 4px;
    }

    .supplier-label {
        font-family: 'S-CoreDream', sans-serif;
        font-weight: 600;
        color: rgba(0, 0, 0, 0.4);
        min-width: 44px;
        margin-right: 6px;
    }

    .supplier-value {
        color: rgba(0, 0, 0, 0.65);
    }

    .supplier-email-text {
        color: rgba(0, 0, 0, 0.65);
    }

    .copy-btn {
        opacity: 0.4;
        transition: opacity 0.2s;
        &:hover { opacity: 1; }
        .v-icon { color: var(--v-primary-base); }
    }
}

/* 기능 카드 */
.feature-card {
    transition: 0.5s !important;
    box-shadow: none !important;
    border: 1px solid rgba(0, 0, 0, 0.07) !important;
    height: 100%;

    .feature-icon { display: block; font-size: 1.7rem !important; }

    .feature-title {
        font-family: 'S-CoreDream', sans-serif;
        font-size: 0.8rem;
        font-weight: 700;
        color: rgba(0, 0, 0, 0.8);
        line-height: 1.4;
        margin-bottom: 4px;
    }

    .feature-desc {
        font-size: 0.72rem;
        color: rgba(0, 0, 0, 0.4);
        line-height: 1.55;
    }
}

.feature-card.feature-on-hover {
    transform: translate(-1%, -1%);
    background-color: var(--v-primary-base) !important;
    box-shadow: 10px 10px 30px rgba(#000, 0.2) !important;
    border-color: transparent !important;
    .feature-title { color: #fff; font-weight: 500; }
    .feature-desc  { color: rgba(#fff, 0.6); }
}

/* Key Screens 타이틀 */
.screens-title-pill {
    font-family: 'Prompt', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 1px;
}

/* Key Screens 카드 */
.screen-card {
    transition: 0.5s !important;
    box-shadow: none !important;
    border: 1px solid rgba(0, 0, 0, 0.07) !important;
    height: 100%;
    position: relative;
    z-index: 1;

    .screen-num {
        font-family: 'Prompt', sans-serif;
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 1px;
        color: rgba(0, 0, 0, 0.3);
    }

    .screen-type-chip {
        font-family: 'S-CoreDream', sans-serif;
        font-size: 0.65rem !important;
        font-weight: 600 !important;
        letter-spacing: 0.3px;
        height: 20px !important;
    }

    .screen-icon { display: block; font-size: 1.7rem !important; }

    .screen-title {
        font-family: 'S-CoreDream', sans-serif;
        font-size: 0.88rem;
        font-weight: 700;
        color: rgba(0, 0, 0, 0.8);
        line-height: 1.4;
        margin-bottom: 4px;
    }

    .screen-desc {
        font-size: 0.72rem;
        color: rgba(0, 0, 0, 0.4);
        line-height: 1.55;
    }

    .screen-view-text {
        font-size: 0.7rem;
        vertical-align: middle;
    }
}

.screen-card.screen-on-hover {
    transform: translate(-1%, -1%);
    background-color: var(--v-primary-base) !important;
    box-shadow: 10px 10px 30px rgba(#000, 0.2) !important;
    border-color: transparent !important;

    .screen-num   { color: rgba(#fff, 0.4); }
    .screen-title { color: #fff; font-weight: 500; }
    .screen-desc  { color: rgba(#fff, 0.6); }
}

@media #{map-get($display-breakpoints, 'xs-only')} {
    .confernote-section { padding-top: 2rem; }
    .product-name { font-size: 1.8rem; }
}</style>